/**
 * WorkflowController — Centralized state machine for Start a Job / estimate /
 * lead / client / project flows.
 */
import React, { useState } from 'react';
import { useIdioma, type Traducir } from '../i18n/idioma';
import { useNavigate } from 'react-router-dom';
import { useCRM } from '../contexts/CRMContext';
import { useEstimates } from '../contexts/EstimatesContext';
import { useNavigation } from '../contexts/NavigationContext';
import { useSettings } from '../contexts/SettingsContext';
import { useEstimateWorkflow } from '../hooks/useEstimateWorkflow';
import { isPricingConfigured } from '../utils/setupChecklist';
import { canEditPricing } from '../utils/estimateEditability';
import { Estimate, ViewState } from '../types';
import { CheckCircle2, Eye, ArrowRight } from 'lucide-react';
import { Button } from './ui/Button';

import LeadModal from './LeadModal';
import ClientModal from './ClientModal';
import CreateEstimateModal, { type JobFlowFinishPayload } from './CreateEstimateModal';
import EstimateCalculator from './EstimateCalculator';
import NewProjectModal from './NewProjectModal';
import QuickPricingSetupModal from './QuickPricingSetupModal';

type WorkflowStep =
    | 'Idle' | 'LeadModal' | 'ClientModal'
    | 'EstimateLauncher'
    | 'EstimateEditor' | 'ProjectModal' | 'PostSave';

interface WorkflowState {
    step: WorkflowStep;
    data?: any;
}

/** Where the estimate editor should return when the user taps Back. */
export interface EditorReturnTarget {
    view: ViewState;
    clientId?: string;
    /** Se salió del tablero de prospectos. Clientes y Prospectos comparten
     *  ruta (`/clients`), así que sin esto Atrás caería en la pestaña de
     *  clientes — donde el prospecto que se acaba de cotizar no aparece. */
    leadId?: string;
}

export interface WorkflowAPI {
    startJobFlow: (preselectedClientId?: string, returnTo?: EditorReturnTarget) => void;
    /** Arma una propuesta para un PROSPECTO. No crea cliente: la propuesta
     *  cuelga del prospecto hasta que el trato se gana. */
    startProposalForLead: (leadId: string, returnTo?: EditorReturnTarget) => void;
    /** @deprecated Use startJobFlow */
    startEstimateFlow: (preselectedClientId?: string) => void;
    openLeadModal: () => void;
    openClientModal: () => void;
    openProjectModal: () => void;
    openEstimateEditor: (estimate: Estimate, returnTo?: EditorReturnTarget) => void;
    triggerQuickAction: (type: 'Job' | 'Estimate' | 'Lead' | 'Client' | 'Project') => void;
    isEditorActive: boolean;
}

function defaultReturnForJob(preselectedClientId?: string): EditorReturnTarget {
    if (preselectedClientId) return { view: 'Clients', clientId: preselectedClientId };
    return { view: 'Dashboard' };
}

/** `t` por parámetro: función suelta, sin hook posible. */
function backLabelForReturn(target: EditorReturnTarget, t: Traducir): string {
    if (target.view === 'Clients' && target.leadId) return t('volver.alProspecto');
    if (target.view === 'Clients') return t('volver.alCliente');
    if (target.view === 'Dashboard') return t('volver.alPanel');
    return t('volver.aEstimados');
}

interface Props {
    children: (api: WorkflowAPI) => React.ReactNode;
}

const WorkflowController: React.FC<Props> = ({ children }) => {
    const { t } = useIdioma();
    const { addLead, addClient } = useCRM();
    const { updateEstimate } = useEstimates();
    const { navigateTo, viewProposal } = useNavigation();
    const navigate = useNavigate();
    const { settings } = useSettings();
    const { createAndOpenEstimate } = useEstimateWorkflow();

    const [workflow, setWorkflow] = useState<WorkflowState>({ step: 'Idle' });
    const [pendingEstimate, setPendingEstimate] = useState<Estimate | null>(null);
    const [showPricingSetup, setShowPricingSetup] = useState(false);
    const [editorReturn, setEditorReturn] = useState<EditorReturnTarget>({ view: 'Estimates' });

    const close = () => setWorkflow({ step: 'Idle' });

    const startJobFlow = (preselectedClientId?: string, returnTo?: EditorReturnTarget) => {
        setEditorReturn(returnTo ?? defaultReturnForJob(preselectedClientId));
        setWorkflow({ step: 'EstimateLauncher', data: { clientId: preselectedClientId } });
    };

    const startProposalForLead = (leadId: string, returnTo?: EditorReturnTarget) => {
        // Vuelve al tablero de prospectos, que es de donde se sale: el cliente
        // todavía no existe, así que no hay ficha de cliente a la que regresar.
        setEditorReturn(returnTo ?? { view: 'Clients', leadId });
        setWorkflow({ step: 'EstimateLauncher', data: { leadId } });
    };

    const handleLauncherFinish = ({
        clientId, clientName, projectType, tradeId, scopeCategoryId, scopeLabel, sourceLeadId,
    }: JobFlowFinishPayload) => {
        const est = createAndOpenEstimate(
            clientId,
            clientName,
            projectType,
            [],
            undefined,
            tradeId,
            scopeCategoryId,
            scopeLabel,
            sourceLeadId
        );
        if (!isPricingConfigured(settings)) {
            setPendingEstimate(est);
            setShowPricingSetup(true);
            setWorkflow({ step: 'Idle' });
            return;
        }
        setTimeout(() => setWorkflow({ step: 'EstimateEditor', data: est }), 50);
    };

    /**
     * La ÚNICA puerta al constructor de presupuestos — y hasta ahora abría
     * cualquier estimado sin mirar su estado.
     *
     * Eso dejaba el candado a medias: el panel del snapshot no deja refrescar
     * las tarifas de un estimado cerrado, pero el constructor entero se abría
     * igual y desde dentro se podía cambiar el precio de cada partida y
     * guardarlo. Lo que el cliente aceptó cambiaba de número, sin firma nueva y
     * sin rastro (PLAN-pricing-model-v2, decisión 7: Sent/Accepted/Paid son de
     * solo lectura).
     *
     * QUÉ SE HACE, Y POR QUÉ ESTO Y NO MÁS. Un estimado cerrado se manda a su
     * PROPUESTA, que es la pantalla que ya existe para leer un documento
     * terminado. Es lo menos invasivo que sostiene el invariante: no inventa un
     * modo «solo lectura» dentro del constructor —que sería una segunda
     * interfaz que mantener, con cada control decidiendo por su cuenta si se
     * deshabilita—, no crea copias a espaldas de nadie, y no añade un diálogo
     * nuevo. El usuario sigue viendo el documento que pidió ver.
     *
     * PARA REVISAR UN CERRADO: duplicar. La lista de estimados ya ofrece
     * «Duplicar» en el menú de la ficha, y la copia nace en Draft — que es
     * justo el paso explícito de «crear revisión» que pide la decisión 7. Se
     * revisa la copia; el original queda como prueba de lo que se ofreció.
     *
     * La regla se lee de utils/estimateEditability: la misma que usan el panel
     * del snapshot y el constructor. Una definición de «cerrado», tres puertas.
     */
    const openEstimateEditor = (est: Estimate, returnTo?: EditorReturnTarget) => {
        if (!canEditPricing(est.status)) {
            viewProposal(est.id);
            return;
        }
        setEditorReturn(returnTo ?? { view: 'Estimates', clientId: est.clientId });
        setWorkflow({ step: 'EstimateEditor', data: est });
    };

    const handleEditorClose = () => {
        const target = editorReturn;
        close();
        if (target.view === 'Clients' && target.leadId) {
            navigate('/clients?tab=leads');
            return;
        }
        if (target.view === 'Clients' && target.clientId) {
            navigate(`/clients?client=${encodeURIComponent(target.clientId)}&tab=estimates`);
            return;
        }
        navigateTo(target.view);
    };

    const proceedAfterPricingSetup = () => {
        if (pendingEstimate) {
            openEstimateEditor(pendingEstimate);
        }
        setPendingEstimate(null);
        setShowPricingSetup(false);
    };

    const handleSaveEstimate = (updated: Estimate, next: 'list' | 'proposal') => {
        updateEstimate(updated.id, updated);
        if (next === 'proposal') {
            viewProposal(updated.id);
            close();
            return;
        }
        setWorkflow({ step: 'PostSave', data: { estimateId: updated.id, clientName: updated.clientName } });
    };

    const api: WorkflowAPI = {
        startJobFlow,
        startProposalForLead,
        startEstimateFlow: startJobFlow,
        openLeadModal: () => setWorkflow({ step: 'LeadModal' }),
        openClientModal: () => setWorkflow({ step: 'ClientModal' }),
        openProjectModal: () => setWorkflow({ step: 'ProjectModal' }),
        openEstimateEditor: (est) => openEstimateEditor(est),
        triggerQuickAction: (type) => {
            if (type === 'Job' || type === 'Estimate') startJobFlow(undefined, { view: 'Dashboard' });
            else if (type === 'Lead') setWorkflow({ step: 'LeadModal' });
            else if (type === 'Client') setWorkflow({ step: 'ClientModal' });
            else if (type === 'Project') setWorkflow({ step: 'ProjectModal' });
        },
        isEditorActive: workflow.step === 'EstimateEditor' && !!workflow.data,
    };

    return (
        <>
            {children(api)}

            {api.isEditorActive && (
                <div className="fixed inset-0 z-[200] bg-acero-50">
                    <EstimateCalculator
                        estimate={workflow.data}
                        backLabel={backLabelForReturn(editorReturn, t)}
                        onClose={handleEditorClose}
                        onSave={(updated) => handleSaveEstimate(updated, 'list')}
                        onSaveAndPreview={(updated) => handleSaveEstimate(updated, 'proposal')}
                    />
                </div>
            )}

            {workflow.step === 'PostSave' && workflow.data?.estimateId && (
                <div className="fixed inset-0 bg-navy-950/50 z-[250] flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl shadow-s3 w-full max-w-md p-8 text-center animate-in zoom-in-95 duration-200">
                        <div className="w-10 h-10 mx-auto mb-4 rounded-xl bg-exito-bg text-exito grid place-items-center">
                            <CheckCircle2 size={20} />
                        </div>
                        <h3 className="text-lg font-semibold tracking-tight text-navy-950 mb-1.5">{t('flujo.guardado.titulo')}</h3>
                        <p className="text-sm text-acero-500 mb-6">
                            {t('flujo.guardado.textoInicio')} {workflow.data.clientName} {t('flujo.guardado.textoFin')}
                        </p>
                        {/* Antes había DOS botones aquí: "Preview & Send Proposal" y
                            "Preview Only". Los dos hacían exactamente lo mismo —
                            abrir la propuesta — y el primero además marcaba el
                            presupuesto como ENVIADO sin mandar ningún correo. El
                            contratista se quedaba esperando una respuesta que nunca
                            iba a llegar, y el presupuesto no llevaba `sentAt`, así
                            que Seguimientos tampoco lo recogía.
                            El sitio correcto ya existe: SidebarShareTab marca ENVIADO
                            con su fecha real en `onSentSuccess`, cuando el correo
                            sale de verdad. Aquí solo se abre la propuesta. */}
                        <div className="flex flex-col gap-2.5">
                            <Button
                                variant="primary"
                                size="lg"
                                className="w-full"
                                leftIcon={<Eye size={18} />}
                                onClick={() => {
                                    viewProposal(workflow.data.estimateId);
                                    close();
                                }}
                            >
                                {t('flujo.guardado.abrirPropuesta')}
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="w-full"
                                rightIcon={<ArrowRight size={14} />}
                                onClick={() => {
                                    navigateTo('Estimates');
                                    close();
                                }}
                            >
                                {t('flujo.guardado.volverEstimados')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {workflow.step === 'LeadModal' && (
                <LeadModal onSave={(data) => addLead(data as any)} onClose={close} />
            )}

            {workflow.step === 'ClientModal' && (
                <ClientModal onSave={(data) => addClient(data as any)} onClose={close} />
            )}

            {workflow.step === 'EstimateLauncher' && (
                <CreateEstimateModal
                    onClose={close}
                    onFinish={handleLauncherFinish}
                    initialClientId={workflow.data?.clientId}
                    initialLeadId={workflow.data?.leadId}
                />
            )}

            {workflow.step === 'ProjectModal' && (
                <NewProjectModal onClose={close} />
            )}

            <QuickPricingSetupModal
                isOpen={showPricingSetup}
                onComplete={proceedAfterPricingSetup}
                onSkip={proceedAfterPricingSetup}
            />
        </>
    );
};

export default WorkflowController;
